import { FC, useEffect, useMemo, useState } from 'react'

import { IcoRemove } from '@/utils/icons'
import cn from './index.module.css'
import Modal from '@common/Modal'
import { useAddAlert } from '@/hooks/useAddAlert'
import { fetchCourseAndTariffs } from './api'
import TariffCard from '@pages/CoursesCatalog/components/TariffCard/TariffCard'
import { useScreenWidth } from '@/hooks/useScreenWidth'
import Preloader from '@UI/Preloader'
import CourseEnrollmentModal from '../CourseEnrollmentModal/CourseEnrollmentModal'
import { CloseOutlined } from '@ant-design/icons'

interface IViewCourseTariffsModal {
  visible: boolean
  courseId: number
  onDismiss: () => void
}

const ViewCourseTariffsModal: FC<IViewCourseTariffsModal> = ({
  visible,
  courseId,
  onDismiss = () => {},
}) => {
  const [enrollingTariff, setEnrollingTariff] = useState<number | null>(null)

  return (
    <>
      <Modal
        active={visible}
        setActive={onDismiss}
        // @ts-ignore
        className={cn.modalWrapper}
      >
        <ModalBody
          courseId={courseId}
          setEnrollingTariff={setEnrollingTariff}
          onDismiss={onDismiss}
        />
      </Modal>
      <CourseEnrollmentModal
        visible={enrollingTariff !== null}
        courseId={courseId}
        tariffId={Number(enrollingTariff)}
        onDismiss={() => setEnrollingTariff(null)}
        onSuccess={() => {}}
      />
    </>
  )
}

function splitIntoThree<T>(arr: T[]): [T[], T[], T[]] {
  const first: T[] = arr.filter((_, index) => index % 3 === 0)
  const second: T[] = arr.filter((_, index) => index % 3 === 1)
  const third: T[] = arr.filter((_, index) => index % 3 === 2)
  return [first, second, third]
}

function splitIntoTwo<T>(arr: T[]): [T[], T[]] {
  const evenIndexed: T[] = arr.filter((_, index) => index % 2 === 0)
  const oddIndexed: T[] = arr.filter((_, index) => index % 2 !== 0)
  return [evenIndexed, oddIndexed]
}

const ModalBody = ({
  courseId,
  setEnrollingTariff,
  onDismiss = () => {},
}: {
  courseId: number
  setEnrollingTariff: (value: React.SetStateAction<number | null>) => void
  onDismiss: () => void
}) => {
  const addAlert = useAddAlert()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [course, setCourse] = useState<ICourse | null>(null)
  const isMediumScreen = useScreenWidth(992)
  const isLargeScreen = useScreenWidth(1280)
  const isMobileScreen = useScreenWidth(768)

  const fetchSettings = async () => {
    setIsLoading(true)
    fetchCourseAndTariffs(courseId)
      .then((course) => {
        if (course) {
          setCourse({
            ...course,
            directionTariffs: course.directionTariffs?.filter(
              (item) => item?.view === 1,
            ),
          })
        }
      })
      .catch((error) => {
        console.error('Ошибка при загрузке данных:', error)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const tariffs = useMemo(() => {
    if (isMobileScreen) return [course?.directionTariffs ?? []]
    if (isMediumScreen) return splitIntoTwo(course?.directionTariffs ?? [])
    if (isLargeScreen) return splitIntoThree(course?.directionTariffs ?? [])
    return splitIntoThree(course?.directionTariffs ?? [])
  }, [isMobileScreen, isMediumScreen, isLargeScreen, course?.directionTariffs])

  return (
    <div className={cn.modalContainer}>
      <div className={cn.modalHeader}>
        <span className={cn.modalHeaderTitle}>Выберите тариф</span>
        <CloseOutlined
          className={cn.closeIcon}
          onClick={onDismiss}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      <div className={cn.contentWrapper}>
        {isLoading && (
          <div className={cn.preloader}>
            <Preloader />
          </div>
        )}
        {!isLoading && !course?.directionTariffs?.length && (
          <div className={cn.stub}>Для этого курса нет тарифов</div>
        )}
        {!isLoading && course?.directionTariffs?.length && (
          <div className={cn.content}>
            {tariffs?.map((list, index) => {
              if (list?.length < 1) return <></>
              return (
                <div className={cn.column} key={index}>
                  {list?.map((tariff) => {
                    return (
                      <TariffCard
                        key={'tariff' + tariff.id}
                        className={cn.item}
                        tariff={tariff}
                        onEnroll={() => setEnrollingTariff(tariff.id)}
                        onDismiss={onDismiss}
                      />
                    )
                  })}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewCourseTariffsModal
